import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import { Form, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { SingleItemSelector } from '@/components/SingleItemSelector'
import { Avatar } from '@/components/Avatar'
import { type CallCenterAddClientDialogViewModel } from './CallCenterAddClientDialogViewModel'
import { AddClientDialog } from '@/features/call-center/AddClientDialog'

export interface AddClientDialogContainerProps {
  vm: CallCenterAddClientDialogViewModel
}

export const AddClientDialogContainer = observer(
  function AddClientDialogContainer({ vm }: AddClientDialogContainerProps) {
    const formId = useId()

    return (
      <AddClientDialog
        showing={vm.modal.showing}
        onClose={vm.close}
        isPending={vm.submit.pending}
        formId={formId}
      >
        <Form id={formId} onSubmit={vm.submit}>
          <FormRow>
            <FormGroupContainer
              label="Email"
              error={vm.validation.getError('email')}
            >
              <TextInputContainer
                model={vm}
                field="email"
                placeholder="client@example.com"
                name="email"
                autoComplete="email"
              />
            </FormGroupContainer>
          </FormRow>
          <FormRow>
            <FormGroupContainer
              label="First name"
              error={vm.validation.getError('firstName')}
            >
              <TextInputContainer
                model={vm}
                field="firstName"
                placeholder="Jane"
                name="firstName"
                autoComplete="given-name"
              />
            </FormGroupContainer>
          </FormRow>
          <FormRow>
            <FormGroupContainer
              label="Last name"
              error={vm.validation.getError('lastName')}
            >
              <TextInputContainer
                model={vm}
                field="lastName"
                placeholder="Doe"
                name="lastName"
                autoComplete="family-name"
              />
            </FormGroupContainer>
          </FormRow>
          <FormRow>
            <FormGroupContainer
              label="Phone"
              error={vm.validation.getError('phone')}
            >
              <TextInputContainer
                model={vm}
                field="phone"
                placeholder="(555) 123-4567"
                name="phone"
                autoComplete="tel"
              />
            </FormGroupContainer>
          </FormRow>
          <FormRow>
            <FormGroupContainer
              label="Workspace"
              error={vm.validation.getError('selectedWorkspace')}
            >
              <SingleItemSelector
                value={vm.selectedWorkspace ?? undefined}
                placeholder="Select workspace"
                onChange={vm.setWorkspace}
                itemOptions={vm.availableWorkspaces}
                itemPrefix={(workspace) =>
                  workspace ? (
                    <Avatar
                      size={24}
                      subject={{ avatar: workspace.branding.logos.icon }}
                    />
                  ) : undefined
                }
                itemToString={(workspace) => workspace?.name || ''}
                testId="custom-select"
              />
            </FormGroupContainer>
          </FormRow>
        </Form>
      </AddClientDialog>
    )
  },
)
